import { z } from 'zod';
import {
  MAX_APPOINTMENTS,
  WARRANTY_STATUS_LABEL,
  WarrantyCategorySchema,
  WarrantyClaimSchema,
  canTransitionClaim,
  claimNeedsReason,
  newestClaimsFirst,
  type WarrantyAppointment,
  type WarrantyClaim,
  type WarrantyStatus,
} from '../../domain/entities/warranty-claim.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged, updateWrite } from '../../ports/batch.ts';
import { NotFoundError } from '../../ports/repository.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';

/**
 * Warranty claims (SCRUM-140, P8-01).
 *
 * The lifecycle is the feature: a homeowner raises a problem, the builder
 * accepts or declines it, a sub is sent, visits are booked, and it is
 * fixed and closed. Each of those is a separate act by a different person,
 * so each is a function rather than a `save` that takes the whole record —
 * a single setter would let a screen move a claim from `submitted` to
 * `closed` with nobody having looked at it.
 */
export class WarrantyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'WarrantyError';
  }
}

export interface WarrantyContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

const scope = (context: WarrantyContext) => ({
  companyId: context.companyId,
  jobId: context.jobId,
});

const claims = (context: WarrantyContext) =>
  context.data.collection<WarrantyClaim>(scope(context), 'warrantyClaims');

export const SubmitClaimInputSchema = z.object({
  title: z.string().trim().min(1, 'A claim needs a title').max(200),
  category: WarrantyCategorySchema,
  description: z.string().trim().max(20_000).default(''),
});
export type SubmitClaimInput = z.input<typeof SubmitClaimInputSchema>;

export async function listWarrantyClaims(context: WarrantyContext): Promise<WarrantyClaim[]> {
  const page = await claims(context).list();
  // Newest first. A warranty list is read to find what has just come in,
  // not to browse a history. `newestClaimsFirst` rather than a sort written
  // here, because the tie on `submittedAt` has to break the same way on
  // both sides of the feature — see its own comment for why there is a tie.
  return newestClaimsFirst(page.items);
}

export async function getWarrantyClaim(
  context: WarrantyContext,
  id: string,
): Promise<WarrantyClaim> {
  const found = await claims(context).get(id);
  if (!found) throw new NotFoundError('warrantyClaims', id);
  return found;
}

/**
 * Raise a claim.
 *
 * **The homeowner is the expected author**, which makes this the first
 * record in the product an external member creates rather than answers.
 * `submittedBy` is kept separately from `createdBy` from the start: they
 * say the same thing today and will not the first time somebody files a
 * claim on an owner's behalf after a phone call, which is most of them.
 *
 * A claim always starts `submitted`. There is no argument for letting the
 * caller choose, and letting it would mean a screen could file one
 * already accepted.
 */
export async function submitWarrantyClaim(
  context: WarrantyContext,
  input: SubmitClaimInput,
  submittedBy = context.uid,
): Promise<WarrantyClaim> {
  const parsed = SubmitClaimInputSchema.safeParse(input);
  if (!parsed.success) throw new WarrantyError(parsed.error.issues[0]!.message);

  const at = context.clock.now().toISOString();
  const claim = WarrantyClaimSchema.parse({
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    title: parsed.data.title,
    category: parsed.data.category,
    description: parsed.data.description,
    status: 'submitted',
    submittedBy,
    submittedAt: at,
    appointments: [],
    linkedRecords: [],
    linkedKeys: [],
    createdAt: at,
    createdBy: context.uid,
    updatedAt: at,
    updatedBy: context.uid,
    version: 1,
  });
  await claims(context).create(claim);
  return claim;
}

async function write(
  context: WarrantyContext,
  existing: WarrantyClaim,
  patch: Partial<WarrantyClaim>,
): Promise<WarrantyClaim> {
  const updated = {
    ...existing,
    ...patch,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };
  await context.data.commit([
    updateWrite<WarrantyClaim>(
      scope(context),
      'warrantyClaims',
      existing.id,
      { ...patch, updatedAt: updated.updatedAt, updatedBy: updated.updatedBy },
      expectUnchanged(existing),
    ),
  ]);
  return updated;
}

/**
 * Move a claim through its lifecycle.
 *
 * **Every refused move names where the claim actually is**, because the
 * common cause of one is two people on the same claim — somebody closed it
 * while you were typing — and "cannot do that" sends them looking for a
 * bug instead of refreshing.
 *
 * `declined` and `resolved` demand a reason. Telling a homeowner their
 * claim is not covered, or that it is fixed, is an answer they are owed in
 * words rather than as a badge changing colour. `accepted` and `closed`
 * do not: accepting is agreeing to look, and closing follows a resolution
 * that already said something.
 */
export async function transitionWarrantyClaim(
  context: WarrantyContext,
  claim: WarrantyClaim,
  to: WarrantyStatus,
  notes?: string,
): Promise<WarrantyClaim> {
  if (!canTransitionClaim(claim.status, to)) {
    throw new WarrantyError(
      `A claim that is ${WARRANTY_STATUS_LABEL[claim.status].toLowerCase()} cannot become ${WARRANTY_STATUS_LABEL[to].toLowerCase()}.`,
    );
  }

  const trimmed = notes?.trim() ?? '';
  if (claimNeedsReason(to) && trimmed.length === 0) {
    throw new WarrantyError(
      to === 'declined'
        ? 'Say why it is not covered. The homeowner is told this.'
        : 'Say what was done to fix it. The homeowner is told this.',
    );
  }

  return write(context, claim, {
    status: to,
    ...(trimmed
      ? {
          resolution: {
            notes: trimmed,
            at: context.clock.now().toISOString(),
            byUid: context.uid,
          },
        }
      : {}),
  });
}

/**
 * Send a trade to look at it.
 *
 * A directory contact rather than a member, because the trade who
 * installed the roof is in the directory whether or not they have a login
 * — and the rules gate a sub's access on this field as a scalar.
 *
 * Only while the claim is open. Assigning somebody to a claim that was
 * declined or closed is either a mistake or a reopening, and the second
 * should be an explicit transition rather than a side effect.
 */
export async function assignWarrantyClaim(
  context: WarrantyContext,
  claim: WarrantyClaim,
  contactId: string | null,
): Promise<WarrantyClaim> {
  if (claim.status === 'declined' || claim.status === 'closed') {
    throw new WarrantyError(
      `${claim.title} is ${WARRANTY_STATUS_LABEL[claim.status].toLowerCase()}. Reopen it before sending somebody.`,
    );
  }
  return write(context, claim, {
    ...(contactId ? { assignedContactId: contactId } : { assignedContactId: undefined }),
  });
}

export const AppointmentInputSchema = z.object({
  on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'A visit needs a date'),
  note: z.string().trim().max(500).default(''),
  contactId: z.string().optional(),
});
export type AppointmentInput = z.input<typeof AppointmentInputSchema>;

/**
 * Book a visit.
 *
 * Appointments live on the claim, so this is a whole-array write guarded
 * by `expectUnchanged` — two people booking at once get a detected
 * conflict rather than a list that silently lost one.
 *
 * **Past dates are allowed.** A visit that already happened is recorded
 * after the fact more often than one is booked ahead, and refusing
 * yesterday would make somebody lie about the date to get it in.
 */
export async function addWarrantyAppointment(
  context: WarrantyContext,
  claim: WarrantyClaim,
  input: AppointmentInput,
): Promise<WarrantyClaim> {
  const parsed = AppointmentInputSchema.safeParse(input);
  if (!parsed.success) throw new WarrantyError(parsed.error.issues[0]!.message);
  if (claim.appointments.length >= MAX_APPOINTMENTS) {
    throw new WarrantyError(`A claim holds at most ${MAX_APPOINTMENTS} visits.`);
  }
  if (claim.status === 'declined' || claim.status === 'closed') {
    throw new WarrantyError(
      `${claim.title} is ${WARRANTY_STATUS_LABEL[claim.status].toLowerCase()}. Reopen it before booking a visit.`,
    );
  }

  const appointment: WarrantyAppointment = {
    id: newId(),
    on: parsed.data.on,
    note: parsed.data.note,
    ...(parsed.data.contactId ? { contactId: parsed.data.contactId } : {}),
  };
  return write(context, claim, { appointments: [...claim.appointments, appointment] });
}

export async function removeWarrantyAppointment(
  context: WarrantyContext,
  claim: WarrantyClaim,
  appointmentId: string,
): Promise<WarrantyClaim> {
  const remaining = claim.appointments.filter((one) => one.id !== appointmentId);
  if (remaining.length === claim.appointments.length) {
    // Refused rather than treated as done. A caller removing something
    // that is not there has stale data, and saying so is how they find out.
    throw new WarrantyError('That visit is not on this claim any more.');
  }
  return write(context, claim, { appointments: remaining });
}

/** Correct the words of a claim, without touching where it is. */
export async function editWarrantyClaim(
  context: WarrantyContext,
  claim: WarrantyClaim,
  input: SubmitClaimInput,
): Promise<WarrantyClaim> {
  const parsed = SubmitClaimInputSchema.safeParse(input);
  if (!parsed.success) throw new WarrantyError(parsed.error.issues[0]!.message);
  return write(context, claim, {
    title: parsed.data.title,
    category: parsed.data.category,
    description: parsed.data.description,
  });
}
